import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import {
  MapPin,
  Buildings,
  Users,
  Folder,
  CurrencyDollar,
  Target,
  TrendUp,
  ChartBar,
  ShareNetwork,
  Link as LinkIcon,
  Copy,
  Trash,
  FilePdf
} from '@phosphor-icons/react';
import { getTerritoryAnalytics, createSharedReport, getSharedReports, deleteSharedReport } from '../services/api';
import apiInstance from '../services/api';
import { Flag } from '../components/Flag';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHero } from '../components/motion/PageHero';

const TERRITORY_COLORS = {
  'Guadeloupe': '#00AEEF',
  'Martinique': '#80C51F',
  'Guyane': '#EA3C3A',
  'Réunion': '#FF8E00',
  'Mayotte': '#D5007D',
};

const COLORS_ARRAY = ['#00AEEF', '#80C51F', '#EA3C3A', '#FF8E00', '#D5007D'];

const TERRITORY_FLAGS = {
  'Guadeloupe': 'gp',
  'Martinique': 'mq',
  'Guyane': 'gf',
  'Réunion': 're',
  'Mayotte': 'yt',
};

const TERRITORY_LABELS_ISO = {
  'Guadeloupe': 'GP',
  'Martinique': 'MQ',
  'Guyane': 'GY',
  'Réunion': 'RE',
  'Mayotte': 'YT',
};

const periodLabels = {
  'all': 'Toutes periodes',
  'month': '30 derniers jours',
  'quarter': '3 derniers mois',
  'year': '12 derniers mois',
  'custom': 'Personnalisée',
};

export default function TerritoryAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');
  const [filterTerritoire, setFilterTerritoire] = useState('all');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [shareDialog, setShareDialog] = useState(false);
  const [shareForm, setShareForm] = useState({ title: 'Rapport d\'impact ESS', territoire: '', expires_days: 30 });
  const [sharedLinks, setSharedLinks] = useState([]);
  const [showLinks, setShowLinks] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = { period };
        if (filterTerritoire && filterTerritoire !== 'all') params.territoire = filterTerritoire;
        if (period === 'custom') {
          if (customStart) params.date_start = customStart;
          if (customEnd) params.date_end = customEnd;
        }
        const res = await getTerritoryAnalytics(params);
        setData(res.data);
      } catch (error) {
        console.error('Error fetching territory analytics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [period, filterTerritoire, customStart, customEnd]);

  const fetchSharedLinks = async () => {
    try {
      const res = await getSharedReports();
      setSharedLinks(res.data.items || []);
    } catch {}
  };

  const handleShare = async () => {
    try {
      const payload = {
        title: shareForm.title,
        territoire: shareForm.territoire || null,
        period: period,
        expires_days: shareForm.expires_days,
      };
      const res = await createSharedReport(payload);
      const link = `${window.location.origin}/rapport/${res.data.token}`;
      await navigator.clipboard.writeText(link);
      toast.success('Lien copie dans le presse-papier !');
      setShareDialog(false);
      fetchSharedLinks();
    } catch (err) {
      toast.error('Erreur lors de la creation du lien');
    }
  };

  const handleDeleteLink = async (id) => {
    try {
      await deleteSharedReport(id);
      toast.success('Lien supprime');
      fetchSharedLinks();
    } catch { toast.error('Erreur'); }
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/rapport/${token}`);
    toast.success('Lien copie !');
  };

  const handleExportPDF = async () => {
    try {
      toast.info('Generation du PDF...');
      const q = new URLSearchParams();
      q.append('period', period);
      if (filterTerritoire && filterTerritoire !== 'all') q.append('territoire', filterTerritoire);
      if (period === 'custom') {
        if (customStart) q.append('date_start', customStart);
        if (customEnd) q.append('date_end', customEnd);
      }
      const res = await apiInstance.get(`/analytics/territories/pdf?${q.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const suffix = filterTerritoire && filterTerritoire !== 'all' ? filterTerritoire.replace(/\s+/g, '_') : 'tous';
      a.download = `rapport_territorial_${period}_${suffix}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF telecharge !');
    } catch {
      toast.error('Erreur lors de la generation du PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-[#5E6D66]">Erreur de chargement des donnees territoriales</p>
      </div>
    );
  }

  const { territories, totals } = data;

  const barChartData = territories.map(t => ({
    name: t.territoire.length > 8 ? t.territoire.slice(0, 8) + '.' : t.territoire,
    fullName: t.territoire,
    Organisations: t.organizations,
    Contacts: t.contacts,
    Dossiers: t.dossiers,
    Opportunites: t.opportunities,
  }));

  const pieOrgData = territories
    .filter(t => t.organizations > 0)
    .map(t => ({
      name: t.territoire,
      value: t.organizations,
    }));

  const piePayData = territories
    .filter(t => t.payments_amount > 0)
    .map(t => ({
      name: t.territoire,
      value: t.payments_amount,
    }));

  const radarData = territories.map(t => {
    const maxOrg = Math.max(...territories.map(x => x.organizations), 1);
    const maxContact = Math.max(...territories.map(x => x.contacts), 1);
    const maxDossier = Math.max(...territories.map(x => x.dossiers), 1);
    const maxOpp = Math.max(...territories.map(x => x.opportunities), 1);
    const maxPay = Math.max(...territories.map(x => x.payments_amount), 1);
    return {
      territoire: t.territoire.length > 6 ? t.territoire.slice(0, 6) + '.' : t.territoire,
      Organisations: Math.round((t.organizations / maxOrg) * 100),
      Contacts: Math.round((t.contacts / maxContact) * 100),
      Dossiers: Math.round((t.dossiers / maxDossier) * 100),
      Paiements: Math.round((t.payments_amount / maxPay) * 100),
    };
  });

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-[#E8E6E1] rounded-lg px-3 py-2 shadow-lg">
          <p className="text-xs font-semibold text-[#1A2421] mb-1">{payload[0]?.payload?.fullName || label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-xs" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-[#E8E6E1] rounded-lg px-3 py-2 shadow-lg">
          <p className="text-xs font-semibold text-[#1A2421]">{payload[0].name}</p>
          <p className="text-xs text-[#5E6D66]">
            {typeof payload[0].value === 'number' && payload[0].value > 100
              ? `${payload[0].value.toLocaleString('fr-FR')} EUR`
              : payload[0].value}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="territory-analytics-page">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Territoires"
        subtitle="Analytics territoriaux Outre-mer et impact local"
        icon={<MapPin size={24} weight="duotone" className="text-white" />}
        gradient="from-[#00AEEF] to-[#2D5A4C]"
        accents={['#80C51F', '#FF8E00']}
      />

      {/* Global Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="territory-totals">
        <Card className="border-[#E8E6E1] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Buildings size={16} className="text-[#2D5A4C]" />
              <span className="text-[10px] uppercase tracking-wider font-bold text-[#5E6D66]">Organisations</span>
            </div>
            <p className="text-2xl font-bold text-[#1A2421]" data-testid="total-organizations">{totals.organizations}</p>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Users size={16} className="text-[#2D5A4C]" />
              <span className="text-[10px] uppercase tracking-wider font-bold text-[#5E6D66]">Contacts</span>
            </div>
            <p className="text-2xl font-bold text-[#1A2421]" data-testid="total-contacts">{totals.contacts}</p>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Folder size={16} className="text-[#2D5A4C]" />
              <span className="text-[10px] uppercase tracking-wider font-bold text-[#5E6D66]">Dossiers</span>
            </div>
            <p className="text-2xl font-bold text-[#1A2421]" data-testid="total-dossiers">{totals.dossiers}</p>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Target size={16} className="text-[#2D5A4C]" />
              <span className="text-[10px] uppercase tracking-wider font-bold text-[#5E6D66]">Opportunites</span>
            </div>
            <p className="text-2xl font-bold text-[#1A2421]" data-testid="total-opportunities">{totals.opportunities}</p>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <CurrencyDollar size={16} className="text-[#2D5A4C]" />
              <span className="text-[10px] uppercase tracking-wider font-bold text-[#5E6D66]">Paiements</span>
            </div>
            <p className="text-2xl font-bold text-[#1A2421]" data-testid="total-payments">{totals.payments_count}</p>
          </CardContent>
        </Card>
        <Card className="border-[#E8E6E1] shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendUp size={16} className="text-[#2D5A4C]" />
              <span className="text-[10px] uppercase tracking-wider font-bold text-[#5E6D66]">Montant total</span>
            </div>
            <p className="text-xl font-bold text-[#1A2421]" data-testid="total-amount">
              {totals.payments_amount.toLocaleString('fr-FR')} EUR
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Territory Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="territory-cards">
        {territories.map((t, idx) => (
          <Card
            key={t.territoire}
            className="border-[#E8E6E1] shadow-sm hover:-translate-y-0.5 transition-transform duration-200 overflow-hidden"
            data-testid={`territory-card-${TERRITORY_LABELS_ISO[t.territoire]}`}
          >
            <div className="h-1.5" style={{ background: COLORS_ARRAY[idx] }} />
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Flag iso={TERRITORY_FLAGS[t.territoire]} size={20} />
                <h3 className="font-semibold text-sm text-[#1A2421]">{t.territoire}</h3>
                <Badge variant="outline" className="ml-auto text-[10px]">{TERRITORY_LABELS_ISO[t.territoire]}</Badge>
              </div>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-[#5E6D66]">Organisations</span>
                  <span className="font-semibold text-[#1A2421]">{t.organizations}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#5E6D66]">Contacts</span>
                  <span className="font-semibold text-[#1A2421]">{t.contacts}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#5E6D66]">Dossiers</span>
                  <span className="font-semibold text-[#1A2421]">{t.dossiers}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#5E6D66]">Dossiers actifs</span>
                  <span className="font-semibold text-[#2D5A4C]">{t.dossiers_actifs}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#5E6D66]">Opportunites</span>
                  <span className="font-semibold text-[#1A2421]">{t.opportunities}</span>
                </div>
                <div className="flex justify-between border-t border-[#E8E6E1] pt-2 mt-2">
                  <span className="text-[#5E6D66]">Montant recu</span>
                  <span className="font-semibold text-[#1A2421]">{t.payments_amount.toLocaleString('fr-FR')} EUR</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bar chart: Comparative */}
        <Card className="border-[#E8E6E1] shadow-sm" data-testid="territory-bar-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#1A2421] flex items-center gap-2">
              <ChartBar size={18} className="text-[#2D5A4C]" />
              Comparaison par territoire
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barChartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#5E6D66' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#5E6D66' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="Organisations" fill="#00AEEF" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="Contacts" fill="#80C51F" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="Dossiers" fill="#EA3C3A" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="Opportunites" fill="#FF8E00" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Pie chart: Organizations distribution */}
        <Card className="border-[#E8E6E1] shadow-sm" data-testid="territory-org-pie">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#1A2421] flex items-center gap-2">
              <Buildings size={18} className="text-[#2D5A4C]" />
              Repartition des organisations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {pieOrgData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieOrgData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      innerRadius={50}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={{ stroke: '#5E6D66', strokeWidth: 1 }}
                    >
                      {pieOrgData.map((entry, index) => (
                        <Cell key={entry.name} fill={TERRITORY_COLORS[entry.name] || COLORS_ARRAY[index % COLORS_ARRAY.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<PieTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-sm text-[#5E6D66]">Aucune donnee disponible</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Second row: Payment pie + Radar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pie: Payments distribution */}
        <Card className="border-[#E8E6E1] shadow-sm" data-testid="territory-pay-pie">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#1A2421] flex items-center gap-2">
              <CurrencyDollar size={18} className="text-[#2D5A4C]" />
              Repartition des paiements recus
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              {piePayData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={piePayData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      innerRadius={50}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={{ stroke: '#5E6D66', strokeWidth: 1 }}
                    >
                      {piePayData.map((entry, index) => (
                        <Cell key={entry.name} fill={TERRITORY_COLORS[entry.name] || COLORS_ARRAY[index % COLORS_ARRAY.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<PieTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-sm text-[#5E6D66]">Aucun paiement recu</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Detailed table */}
        <Card className="border-[#E8E6E1] shadow-sm" data-testid="territory-detail-table">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#1A2421] flex items-center gap-2">
              <MapPin size={18} className="text-[#2D5A4C]" />
              Detail par territoire
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[#E8E6E1]">
                    <th className="text-left py-2 pr-3 text-[#5E6D66] font-semibold uppercase tracking-wider whitespace-nowrap">Territoire</th>
                    <th className="text-right py-2 px-2 text-[#5E6D66] font-semibold uppercase tracking-wider whitespace-nowrap">Orgs</th>
                    <th className="text-right py-2 px-2 text-[#5E6D66] font-semibold uppercase tracking-wider whitespace-nowrap">Contacts</th>
                    <th className="text-right py-2 px-2 text-[#5E6D66] font-semibold uppercase tracking-wider whitespace-nowrap">Dossiers</th>
                    <th className="text-right py-2 px-2 text-[#5E6D66] font-semibold uppercase tracking-wider whitespace-nowrap">Opps</th>
                    <th className="text-right py-2 pl-2 text-[#5E6D66] font-semibold uppercase tracking-wider whitespace-nowrap">Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {territories.map((t, idx) => (
                    <tr key={t.territoire} className="border-b border-[#F0EFEB] last:border-0 hover:bg-[#F9F8F5] transition-colors">
                      <td className="py-2.5 pr-3">
                        <div className="flex items-center gap-2">
                          <Flag iso={TERRITORY_FLAGS[t.territoire]} size={14} />
                          <span className="font-medium text-[#1A2421]">{t.territoire}</span>
                        </div>
                      </td>
                      <td className="text-right py-2.5 px-2 text-[#1A2421] font-medium">{t.organizations}</td>
                      <td className="text-right py-2.5 px-2 text-[#1A2421] font-medium">{t.contacts}</td>
                      <td className="text-right py-2.5 px-2 text-[#1A2421] font-medium">{t.dossiers}</td>
                      <td className="text-right py-2.5 px-2 text-[#1A2421] font-medium">{t.opportunities}</td>
                      <td className="text-right py-2.5 pl-2 text-[#1A2421] font-semibold">{t.payments_amount.toLocaleString('fr-FR')} EUR</td>
                    </tr>
                  ))}
                  <tr className="bg-[#F9F8F5] font-semibold">
                    <td className="py-2.5 pr-3 text-[#1A2421]">Total</td>
                    <td className="text-right py-2.5 px-2 text-[#1A2421]">{totals.organizations}</td>
                    <td className="text-right py-2.5 px-2 text-[#1A2421]">{totals.contacts}</td>
                    <td className="text-right py-2.5 px-2 text-[#1A2421]">{totals.dossiers}</td>
                    <td className="text-right py-2.5 px-2 text-[#1A2421]">{totals.opportunities}</td>
                    <td className="text-right py-2.5 pl-2 text-[#1A2421]">{totals.payments_amount.toLocaleString('fr-FR')} EUR</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Shared Links Panel */}
      {showLinks && (
        <Card className="border-[#E8E6E1] shadow-sm" data-testid="shared-links-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#1A2421] flex items-center gap-2">
              <LinkIcon size={18} className="text-[#2D5A4C]" />
              Liens partages
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sharedLinks.length === 0 ? (
              <p className="text-sm text-[#5E6D66] text-center py-4">Aucun lien partage pour le moment.</p>
            ) : (
              <div className="space-y-2">
                {sharedLinks.map((link) => (
                  <div key={link.id} className="flex items-center justify-between p-3 border border-[#E8E6E1] rounded-lg" data-testid={`shared-link-${link.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[#1A2421] truncate">{link.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {link.territoire && <Badge variant="outline" className="text-[10px]">{link.territoire}</Badge>}
                        <span className="text-[10px] text-[#5E6D66]">{link.view_count} vue(s)</span>
                        {link.expires_at && (
                          <span className="text-[10px] text-[#5E6D66]">
                            Expire le {new Date(link.expires_at).toLocaleDateString('fr-FR')}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      <Button variant="ghost" size="sm" onClick={() => copyLink(link.token)} data-testid={`copy-link-${link.id}`}>
                        <Copy size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteLink(link.id)} className="text-red-500" data-testid={`delete-link-${link.id}`}>
                        <Trash size={14} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Share Dialog */}
      <Dialog open={shareDialog} onOpenChange={setShareDialog}>
        <DialogContent className="max-w-md" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Partager le rapport d'impact</DialogTitle>
            <DialogDescription className="sr-only">Contenu de la boite de dialogue</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Titre du rapport</Label>
              <Input
                value={shareForm.title}
                onChange={(e) => setShareForm(prev => ({ ...prev, title: e.target.value }))}
                data-testid="share-title-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Territoire (optionnel)</Label>
              <Select value={shareForm.territoire || 'all'} onValueChange={(v) => setShareForm(prev => ({ ...prev, territoire: v === 'all' ? '' : v }))}>
                <SelectTrigger data-testid="share-territoire-select">
                  <SelectValue placeholder="Tous les territoires" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les territoires</SelectItem>
                  <SelectItem value="Guadeloupe">Guadeloupe</SelectItem>
                  <SelectItem value="Martinique">Martinique</SelectItem>
                  <SelectItem value="Guyane">Guyane</SelectItem>
                  <SelectItem value="Réunion">Reunion</SelectItem>
                  <SelectItem value="Mayotte">Mayotte</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Expiration (jours)</Label>
              <Input
                type="number"
                value={shareForm.expires_days}
                onChange={(e) => setShareForm(prev => ({ ...prev, expires_days: parseInt(e.target.value) || 30 }))}
                data-testid="share-expires-input"
              />
            </div>
            <p className="text-xs text-[#5E6D66]">
              Un lien public sera genere. Toute personne disposant du lien pourra consulter le rapport sans authentification.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareDialog(false)}>Annuler</Button>
            <Button onClick={handleShare} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="share-confirm-btn">
              <ShareNetwork size={16} className="mr-1" />
              Generer le lien
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
